<template>
  <div
    class="fullCalendarAvailability"
    :class="`${$mq}Mode`"
  >
    <FullCalendar
      ref="fullAvailability"
      default-view="timeGridWeek"
      :display-event-end="true"
      :header="header"
      :plugins="calendarPlugins"
      :events="calendarEvents"
      :event-constraint="eventConstraint"
      :event-render="eventRender"
      :event-overlap="false"
      :selectable="true"
      :select-min-distance="20"
      :select-overlap="false"
      :select-constraint="selectConstraint"
      :slot-event-overlap="false"
      :now-indicator="false"
      :first-day="1"
      :all-day-slot="false"
      :column-header-format="columnHeaderFormat"
      :event-time-format="eventTimeFormat"
      :slot-label-format="slotLabelFormat"
      :scroll-time="'08:30:00'"
      @dateClick="handleDateClick"
      @eventDrop="rescheduleEvent"
      @eventResize="rescheduleEvent"
      @eventClick="removeEvent"
      @select="handleSelect"
    />
  </div>
</template>

<script>
import FullCalendar from '@fullcalendar/vue';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import rrulePlugin from '@fullcalendar/rrule';

export default {
  components: {
    FullCalendar,
  },

  props: {
    recurringAvailabilities: {
      type: Array,
      default() {
        return [];
      },
    },

    lastId: {
      type: Number,
      default: 0,
    },

    canSaveRecurring: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      header: {
        left: '',
        center: '',
        right: '',
      },
      calendarPlugins: [
        timeGridPlugin,
        interactionPlugin,
        rrulePlugin,
      ],
      eventConstraint: { startTime: '0:00', endTime: '24:00' },
      selectConstraint: { startTime: '0:00', endTime: '24:00' },
      columnHeaderFormat: { weekday: 'short' },
      eventTimeFormat: { hour: '2-digit', minute: '2-digit', hour12: false },
      slotLabelFormat: { hour: '2-digit', minute: '2-digit', hour12: false },
      // calendarEvents: [],
      index: 0,
    };
  },

  computed: {
    calendarEvents: {
      get() {
        return this.recurringAvailabilities;
      },
      set(events) {
        this.$emit('update:recurringAvailabilities', events);
      },
    },
  },

  methods: {
    eventRender(info) {
      // fix a fullcalendar bug where an event has duration of 24 hours,
      // it display only the start time
      const momentStart = this.$moment(info.event.start);
      const momentEnd = this.$moment(info.event.end);

      // make sure event cannot be on 2 days
      if (momentStart.day() !== momentEnd.day()) {
        const newEnd = momentStart.clone().hour(23).minute(59);

        const timeLabel = info.el.querySelector('.fc-time');
        if (timeLabel) {
          timeLabel.innerHTML = `${momentStart.format('HH:mm')} - ${newEnd.format('HH:mm')}`;
        }
      }
    },

    addAvailability(start, end) {
      const calEvents = this.calendarEvents;
      const momentStart = this.$moment(start);
      // avoid double event due similar event click / select
      const overlap = calEvents.some((el) => {
        if (el.saveDay === momentStart.format('dddd').toLowerCase()) {
          const [startHour, startMinute] = el.saveTime.split(':');
          const startTime = this.$moment()
            .day(el.saveDay)
            .hour(startHour)
            .minute(startMinute);

          const [durationHour, durationMinute] = el.duration.split(':');
          const endTime = startTime.clone()
            .add(durationHour, 'hours')
            .add(durationMinute - 1, 'minutes'); // -1 is a trick: an event that start at 4 shouldnt be blocked because another event end at 4

          return momentStart.isBetween(
            startTime,
            endTime,
            'minutes',
            '[]',
          );
        }
        return false;
      });
      if (overlap) {
        return;
      }

      let momentEnd = this.$moment(end);
      let duration = this.$moment.duration(momentEnd.diff(momentStart, 'hours:minutes'));

      // dont allow event shorter than 90 minutes (except after 22:30)
      if (duration.asMinutes() < 90
          && momentStart.isBefore(momentStart.clone().hours(22).minutes(30))) {
        momentEnd = momentStart.clone().add(90, 'minutes');
        duration = this.$moment.duration(momentEnd.diff(momentStart, 'hours:minutes'));
      }

      calEvents.push({ // add new event data
        allDay: false,
        backgroundColor: 'green',
        borderColor: 'green',
        editable: true,
        id: this.lastId,
        rrule: `DTSTART:20120101T${momentStart.format('HHmm')}00
RRULE:FREQ=WEEKLY;INTERVAL=1;BYDAY=${momentStart.format('dd').toUpperCase()}`,
        saveDay: momentStart.format('dddd').toLowerCase(),
        saveTime: momentStart.format('HH:mm'),
        duration: `${Math.floor(duration.asHours())}:${duration.minutes().toString().padStart(2, '0')}`,
      });

      this.calendarEvents = this.mergeEvents(calEvents);
      this.$emit('update:canSaveRecurring', true);
      this.$emit('update:lastId', this.lastId + 1);
    },

    mergeEvents(events) {
      // this function format the list of events and merge
      // the ones that are overlapping or adjacent
      // by ordering the events chronologically and comparing the end date of an event
      // with the start date of the next event
      const mergedAvailabilities = [];

      // we first need to order by day and time
      const sortedAvailabilities = events.sort((a, b) => {
        const [aHours, aMinutes] = a.saveTime.split(':');
        const [bHours, bMinutes] = b.saveTime.split(':');
        const aTime = this.$moment().day(a.saveDay).hour(aHours).minute(aMinutes);
        const bTime = this.$moment().day(b.saveDay).hour(bHours).minute(bMinutes);

        if (aTime.isBefore(bTime)) {
          return -1;
        }
        return 1;
      });

      // Check for adjacent/overlapping Event in the newly ordered list
      sortedAvailabilities.forEach((avail) => {
        let lastIsAdjacent = false;
        let lastAvail = null;
        if (mergedAvailabilities.length > 0) {
          lastAvail = mergedAvailabilities[mergedAvailabilities.length - 1];
          if (lastAvail.saveDay === avail.saveDay) {
            const [lastHour, lastMinute] = lastAvail.saveTime.split(':');
            const [lastDurationHour, lastDurationMinute] = lastAvail.duration.split(':');
            const lastAvailStartTime = this.$moment()
              .day(lastAvail.saveDay)
              .hour(lastHour)
              .minute(lastMinute);

            const lastAvailEndTime = lastAvailStartTime.clone()
              .add(lastDurationHour, 'hours')
              .add(parseInt(lastDurationMinute, 10) + 1, 'minutes');

            const [thisHour, thisMinute] = avail.saveTime.split(':');
            const thisAvailStartTime = this.$moment()
              .day(avail.saveDay)
              .hour(thisHour)
              .minute(thisMinute);

            lastIsAdjacent = thisAvailStartTime.isBetween(
              lastAvailStartTime,
              lastAvailEndTime,
              'minutes',
              '[]',
            );
          }
        }

        // Add new events in the new ordered list or add duration the previous adjacent event
        if (!lastIsAdjacent) {
          mergedAvailabilities.push({ ...avail });
        } else {
          // last event should end when this event is ending
          const [lastHour, lastMinute] = lastAvail.saveTime.split(':');
          const [thisHour, thisMinute] = avail.saveTime.split(':');
          const [thisDurationHour, thisDurationMinute] = avail.duration.split(':');
          const lastAvailStartTime = this.$moment()
            .day(lastAvail.saveDay)
            .hour(lastHour)
            .minute(lastMinute);
          const thisAvailEndTime = this.$moment()
            .day(avail.saveDay)
            .hour(thisHour)
            .minute(thisMinute)
            .add(thisDurationHour, 'hours')
            .add(thisDurationMinute, 'minutes');
          const newDuration = this.$moment.duration(thisAvailEndTime.diff(lastAvailStartTime, 'hours:minutes'));
          lastAvail.duration = `${Math.floor(newDuration.asHours())}:${newDuration.minutes().toString().padStart(2, '0')}`;
        }
      });

      return mergedAvailabilities;
    },

    handleDateClick(arg) {
      let endDate;
      if (this.$moment(arg.date).isBefore(this.$moment(arg.date).hours(22).minutes(30))) {
        endDate = this.$moment(arg.date).add(90, 'm').toDate();
      } else {
        endDate = this.$moment(arg.date).hours(23).minutes(59).toDate();
      }
      this.addAvailability(arg.date, endDate);
    },

    handleSelect(arg) {
      // console.log('select', arg);
      this.addAvailability(arg.start, arg.end);
    },

    rescheduleEvent(arg) {
      // console.log('arg.event', arg.event);
      const index = this.calendarEvents.findIndex((el) => el.id === parseInt(arg.event.id, 10));

      const momentStart = this.$moment(arg.event.start);
      let momentEnd = this.$moment(arg.event.end);
      let duration = this.$moment.duration(momentEnd.diff(momentStart, 'hours:minutes'));

      if (duration.asMinutes() < 90) {
        momentEnd = momentStart.clone().add(90, 'minutes');
        duration = this.$moment.duration(momentEnd.diff(momentStart, 'hours:minutes'));
      }

      this.calendarEvents[index].rrule = `DTSTART:20120101T${momentStart.format('HHmm')}00
RRULE:FREQ=WEEKLY;INTERVAL=1;BYDAY=${momentStart.format('dd').toUpperCase()}`;
      this.calendarEvents[index].duration = `${Math.floor(duration.asHours())}:${duration.minutes().toString().padStart(2, '0')}`;
      this.calendarEvents[index].saveDay = momentStart.format('dddd').toLowerCase();
      this.calendarEvents[index].saveTime = momentStart.format('HH:mm');
      this.calendarEvents = this.mergeEvents(this.calendarEvents);
      this.$emit('update:canSaveRecurring', true);
    },

    removeEvent(arg) {
      const filteredEvents = this.calendarEvents.filter(
        (el) => el.id !== parseInt(arg.event.id, 10),
      );

      this.calendarEvents = this.mergeEvents(filteredEvents);
      this.$emit('update:canSaveRecurring', true);
    },
  },
};
</script>

<style lang='scss'>
@import '~@fullcalendar/core/main.css';
@import '~@fullcalendar/timegrid/main.css';

.fullCalendarAvailability {
  // My availability - Recurring
  .fc {
    .fc-view-container {
      background-color: white;
      color: black;

      .fc-head {
        .fc-day-header {
          font-weight: normal;
          font-size: 12px;
          height: 25px;
          padding: 2px 0;
          vertical-align: middle;
        }
      }
    }

    .fc-today {
      border: none !important;
      border-top: 1px solid #ddd !important;
      font-weight: bold;
    }

    .fc-content {
      position: sticky;
      top: 0;

      .fc-time {
        font-size: 12px;
        text-align: center;

        span {
          display: inline-block;
          word-break: break-word;
          white-space: normal;
        }
      }
    }
  }

  &.tabletMode {
    .fc {
      .fc-view-container {
        overflow-x: scroll;

        .fc-view.fc-timeGridWeek-view.fc-timeGrid-view {
          width: 200%;
        }
      }

      .fc-scroller.fc-time-grid-container {
        min-height: 525px;
      }
    }
  }

  &.mobileMode {
    .fc {
      .fc-scroller.fc-time-grid-container {
        min-height: 360px;
      }

      .fc-view-container {
        table {
          font-size: 0.85em;
        }
      }
    }
  }
}

</style>
